import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { ApiClientConfig, ApiLogger, TokenProvider } from '@klareospace/pharos-lib-types';
import { DEFAULT_HTTP_TIMEOUT_MS } from '../constants';
import { generateCorrelationId, isAxiosError } from './api-error';

interface ApiClientOptions {
  config: ApiClientConfig;
  /** Prefix for correlation IDs (e.g., "bo", "fo", "drv") */
  correlationIdPrefix?: string;
  logger?: ApiLogger;
  /** Optional callback invoked every time a request is sent. Used to notify auth provider of backend activity. */
  onRequestSent?: () => void;
  /** Callback invoked on 401 responses. Used to trigger sign-out when the user's session is invalidated server-side. */
  onUnauthorized?: () => void;
  tokenProvider?: TokenProvider;
}

interface RequestMetadata {
  startTime: number;
}

type ConfigWithMetadata = InternalAxiosRequestConfig & { _metadata?: RequestMetadata };

export class ApiClient {
  readonly instance: AxiosInstance;
  private readonly correlationIdPrefix: string;
  private readonly logger?: ApiLogger;
  private readonly onRequestSent?: () => void;
  private readonly onUnauthorized?: () => void;
  private readonly tokenProvider?: TokenProvider;

  constructor(options: ApiClientOptions) {
    const { config, correlationIdPrefix = 'app', logger, onRequestSent, onUnauthorized, tokenProvider } = options;

    this.correlationIdPrefix = correlationIdPrefix;
    this.logger = logger;
    this.onRequestSent = onRequestSent;
    this.onUnauthorized = onUnauthorized;
    this.tokenProvider = tokenProvider;

    this.instance = axios.create({
      baseURL: config.baseURL,
      headers: {
        'Content-Type': 'application/json',
        ...config.defaultHeaders,
      },
      timeout: config.timeout ?? DEFAULT_HTTP_TIMEOUT_MS,
    });

    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Execute a request and return the response data.
   * Errors propagate naturally — error handling is done at the hook/component level.
   */
  async execute<T>(call: () => Promise<AxiosResponse<T>>): Promise<T> {
    const response = await call();
    return response.data;
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private setupRequestInterceptor(): void {
    this.instance.interceptors.request.use(async (config: ConfigWithMetadata) => {
      config._metadata = { startTime: Date.now() };

      const correlationId = generateCorrelationId(this.correlationIdPrefix);
      config.headers.set('x-correlation-id', correlationId);

      if (this.tokenProvider) {
        const token = await this.tokenProvider();
        if (token !== null) {
          config.headers.set('Authorization', `Bearer ${token}`);
        }
      }

      if (this.logger?.onRequest) {
        const method = config.method ?? 'unknown';
        const url = config.url ?? '';
        this.logger.onRequest(method, url);
      }

      this.onRequestSent?.();

      return config;
    });
  }

  private setupResponseInterceptor(): void {
    this.instance.interceptors.response.use(
      (response) => {
        this.logResponse(response);
        return response;
      },
      (error: unknown) => {
        this.logError(error);
        this.handleUnauthorized(error);
        return Promise.reject(error);
      },
    );
  }

  private handleUnauthorized(error: unknown): void {
    if (!this.onUnauthorized) {
      return;
    }
    if (isAxiosError(error) && error.response?.status === 401) {
      this.onUnauthorized();
    }
  }

  private logResponse(response: { config: ConfigWithMetadata; status: number }): void {
    const config = response.config;
    const durationMs = Date.now() - (config._metadata?.startTime ?? Date.now());

    if (this.logger?.onResponse) {
      const method = config.method ?? 'unknown';
      const url = config.url ?? '';
      this.logger.onResponse(method, url, response.status, durationMs);
    }
  }

  private extractErrorInfo(error: unknown): {
    durationMs: number;
    method: string;
    status: number | undefined;
    url: string;
  } {
    if (!isAxiosError(error)) {
      return { durationMs: 0, method: 'unknown', status: undefined, url: '' };
    }
    const config = error.config as ConfigWithMetadata | undefined;
    return {
      durationMs: Date.now() - (config?._metadata?.startTime ?? Date.now()),
      method: config?.method ?? 'unknown',
      status: error.response?.status,
      url: config?.url ?? '',
    };
  }

  private logError(error: unknown): void {
    if (!this.logger?.onError) {
      return;
    }
    const { durationMs, method, status, url } = this.extractErrorInfo(error);
    this.logger.onError(method, url, status, durationMs, error);
  }
}
